import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { selectParticipantAttendedModules } from '../../../../store/courseSelectors';
import { clearSteps, deleteStep, loadSteps } from '../../../../store/participantService';
import { scrollToFocus } from '../../../../utils/eventUtils';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../../utils/formValidation/commonConstants';
import { STEPS_ERROR } from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import Button from '../../../formElements/Button';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import EditSteps from './EditSteps';
import StepsRow from './StepsRow';

import app from '../../../../app.module.css';
import local from '../wellbeing.module.css';

const headCells = [
    { id: 'date', numeric: false, label: 'Date' },
    { id: 'value', numeric: true, label: 'Value' },
    { id: 'linkedModule', numeric: false, label: 'Linked Module' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'date',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const Steps = ({
    participant,
    isEditDisabled = true,
    isDeleteDisabled = true,
    isCreateDisabled = true,
    linkedModuleId
}) => {
    const dispatch = useDispatch();
    const stepsRef = useRef(null);

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [modules, setModules] = useState([]);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const [loadingContentMessage, setLoadingContentMessage] = useState('');

    // STORE STATE
    const attendedModules = useSelector(selectParticipantAttendedModules);
    const steps = useSelector((state) => state.entities.participantService.steps);
    const stepsMetaData = useSelector((state) => state.entities.participantService.stepsMetaData);

    // USEEFFECTS
    useEffect(
        () => () => {
            cleanUp();
        },
        []
    );

    useEffect(() => {
        participant?.id && dispatch(loadSteps(participant.id));
    }, [participant?.id]);

    useEffect(() => {
        if (stepsMetaData?.loaded) {
            let modules = [];
            if (!steps?.length) {
                setRows([]);
                if (attendedModules?.length) modules = [...attendedModules];
                setLoadingContentMessage(STEPS_ERROR);
            } else {
                setRows(
                    steps.map((el) => ({
                        ...el,
                        linkedModule: el.linkedModuleId
                            ? attendedModules?.find((entry) => entry.id === el.linkedModuleId)
                                  ?.title
                            : undefined
                    }))
                );

                if (attendedModules?.length) {
                    modules = attendedModules.filter(
                        (el) => !steps.find((entry) => entry.linkedModuleId === el.id)
                    );
                }
            }
            setModules(modules.map((el) => ({ ...el, name: el.title })) || []);
        }
    }, [steps, stepsMetaData, attendedModules]);

    // FNs
    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <StepsRow
                key={el.id}
                row={el}
                isDisabled={isEditDisabled || showForm}
                isDeleteDisabled={isDeleteDisabled || showForm}
                onEdit={onEdit}
                onDelete={onDelete}
            />
        ));

    const cleanUp = () => {
        dispatch(clearSteps());
    };

    // EVENT HANDLERS
    const onCreate = () => {
        setFormType('create');
        setSelectedRow({});
        setShowForm(true);
    };

    const onEdit = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setFormType('edit');
        setShowForm(true);
    };

    const onDelete = (row) => {
        setSelectedRow(row);
        dispatch(deleteStep(row.id));
    };

    const onClose = () => {
        setShowForm(false);
        setSelectedRow({});
        scrollToFocus(stepsRef);
    };

    return (
        <div className={local.wellBeingSection} data-testid="steps" ref={stepsRef}>
            <h2 className={app.sectionHeading}>Steps</h2>
            <div className={local.tableSection}>
                {rows.length < 1 ? (
                    <LoadingSpinner content={loadingContentMessage} />
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
            <div>
                {!showForm ? (
                    <Button
                        id="addStepsButton"
                        type="button"
                        content="Add New Steps Value"
                        disabled={isCreateDisabled}
                        icon={true}
                        onClick={onCreate}
                    />
                ) : (
                    <EditSteps
                        participant={participant}
                        onClose={onClose}
                        isDisabled={
                            (formType === 'edit' && isEditDisabled) ||
                            (formType === 'create' && isCreateDisabled)
                        }
                        attendedModules={modules}
                        linkedModuleId={linkedModuleId || ''}
                        formType={formType}
                        row={selectedRow}
                    />
                )}
            </div>
        </div>
    );
};

Steps.propTypes = {
    participant: PropTypes.object.isRequired,
    isEditDisabled: PropTypes.bool,
    isDeleteDisabled: PropTypes.bool,
    isCreateDisabled: PropTypes.bool,
    linkedModuleId: PropTypes.string
};

export default Steps;
